import type { Transformer } from './transformer'

/**
 * Combines multiple {@link Transformer transformers} with different output
 * types into one {@link Transformer} that can be used for any of those output
 * types
 *
 * **NOTE:** be careful not to pass multiple transformers of the same output
 * type since only one of those transformers' transformation method will
 * actually be used
 *
 * @example
 * const bidirectionalTransformer = combine(inputTransformer, outputTransformer)
 *
 * bidirectionalTransformer.getInput(...) === inputTransformer.getInput(...)
 * bidirectionalTransformer.getOutput(...) === outputTransformer.getOutput(...)
 */
export function combine(): object
// NOTE: these overloads are mostly just relevant when in a deferred evaluation
// context (i.e. function or class with a type parameter). The last overload
// should be equivalent to these overloads in a normal context
export function combine<TTransformer extends Transformer.Constraint>(
  transformer: TTransformer,
): TTransformer
export function combine<
  T1 extends Transformer.Constraint,
  T2 extends Transformer.Constraint,
>(transformer1: T1, transformer2: T2): T1 & T2
export function combine<
  T1 extends Transformer.Constraint,
  T2 extends Transformer.Constraint,
  T3 extends Transformer.Constraint,
>(transformer1: T1, transformer2: T2, transformer3: T3): T1 & T2 & T3
export function combine<
  T1 extends Transformer.Constraint,
  T2 extends Transformer.Constraint,
  T3 extends Transformer.Constraint,
  T4 extends Transformer.Constraint,
>(
  transformer1: T1,
  transformer2: T2,
  transformer3: T3,
  transformer4: T4,
): T1 & T2 & T3 & T4
export function combine<
  TTransformers extends readonly Transformer.Constraint[],
>(...transformers: TTransformers): UnionToIntersection<TTransformers[number]>
export function combine(...transformers: readonly Transformer[]): Transformer {
  return transformers.reduce(
    (prev, transformer) => Object.assign(prev, transformer),
    {},
  )
}
